<template>
  <div>
    <basic-portlet title="Plans" :show-footer="false">
      <div class="row">
        <div class="col-12 col-md-4">
          <multiselect v-model="selected_plan"
                       label="label"
                       track-by="id"
                       :options="plans"
                       :multiple="false"
                       :allow-empty="false"
                       placeholder="Choose a plan to edit"
                       @select="populateSelectedFeatures" />
        </div>
        <div class="col-12 col-md-8">
          <button class="btn btn-ss-standout" @click="$modal.show(`create-plan-modal`)">
            + Plan
          </button>
        </div>
      </div>

      <div v-if="selected_plan" class="row mt-4">
        <div class="col-12 col-md-6">
          <div class="form-group">
            <label>Plan Name</label>
            <input v-model="selected_plan.label"
                   type="text"
                   class="form-control"
                   autocomplete="off">
          </div>
        </div>

        <div class="col-12 col-md-6">
          <div class="alert alert-secondary" role="alert">
            <div class="alert-text">
              <p class="kt-font-bold">
                Customers :
              </p>
              <div v-if="selected_plan.customers && selected_plan.customers.length">
                <span v-for="customer in selected_plan.customers" :key="customer.uuid">{{ customer.name }} <br></span>
              </div>
              <span v-else>Plan not used by any customers</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selected_plan" class="row">
        <div class="col-12 col-md-6">
          <basic-portlet title="Toggleable Features" :show-footer="false" :bordered="true">
            <div class="form-group">
              <div class="d-flex flex-column">
                <ss-checkbox v-for="feature in unfixed_features"
                             :id="`${selected_plan.slug}_unfixed_feature_${feature.id}`" :key="feature.id"
                             v-model="staged_features[feature.id]">
                  {{ feature.label }}
                </ss-checkbox>
              </div>
            </div>
          </basic-portlet>
        </div>
        <div class="col-12 col-md-6">
          <basic-portlet title="Fixed Features" caption="Features that must be enabled at all times."
                         :show-footer="false" :bordered="true">
            <div class="form-group">
              <div class="d-flex flex-column">
                <ss-checkbox v-for="feature in fixed_features" :id="`${selected_plan.slug}_fixed_feature_${feature.id}`"
                             :key="feature.id" :value="staged_features[feature.id]"
                             :disabled="true">
                  {{ feature.label }}
                </ss-checkbox>
              </div>
            </div>
          </basic-portlet>

          <basic-portlet title="Allowed Feed Types" caption=""
                         :show-footer="false"
                         :bordered="true">
            <div class="form-group">
              <div class="d-flex flex-column">
                <ss-checkbox v-for="feed_type in feed_types" :id="`${selected_plan.slug}_feed_type_${feed_type.id}`"
                             :key="feed_type.id" v-model="allowed_feed_types[feed_type.id]"
                             :disabled="false">
                  {{ feed_type.label }}
                </ss-checkbox>
              </div>
            </div>
          </basic-portlet>
        </div>
      </div>
      <div v-if="selected_plan" class="row">
        <div class="col-12 col-md-6">
          <multiselect
            v-model="selected_plan_type"
            :options="plan_types"
            :multiple="false"
            :allow-empty="true"
            placeholder="Choose a plan type" />
        </div>
      </div>
      <div v-if="selected_plan" class="row mt-4">
        <div class="col-12">
          <button class="btn btn-ss-standout" @click="updatePlan">
            Update
          </button>
          <button v-if="!selected_plan.customers || !selected_plan.customers.length" class="btn btn-ss-danger"
                  @click="deletePlan">
            <i class="la la-trash" /> Delete
          </button>
          <span v-else>To delete plan, reassign customers to a different plan.</span>
        </div>
      </div>
    </basic-portlet>
    <create-plan-modal />
  </div>
</template>

<script>
import BasicPortlet from "VueComponents/BasicPortlet";
import Multiselect from "vue-multiselect";
import CreatePlanModal from "VuePartials/Admin/VJSModals/CreatePlanModal";

import PlanTypesMixin from "VueMixins/PlanTypesMixin";

/* webpack-strip-block:removed */

export default {
  components: { BasicPortlet, Multiselect, CreatePlanModal },
  mixins: [PlanTypesMixin],
  data: () => ({
    plans: window.plans,
    features: window.features,
    feed_types: window.feed_types,
    selected_plan: null,
    staged_features: null, // mutable reflection of this.features
    allowed_feed_types: {},
  }),
  computed: {
    /**
     * @type Feature[]
     * Features which are not modifiable by the user.
     */
    fixed_features() {
      return _.filter(this.features, { 'fixed': 1 });
    },
    /**
     * @type Feature[]
     * Features which the user may toggle on or off.
     */
    unfixed_features() {
      return _.filter(this.features, { 'fixed': 0 });
    },
    /**
     * @type number[]
     * The feature ids which have been selected by the user.
     */
    enabled_staged_feature_ids() {
      return Object.entries(this.staged_features ?? {}) // just return the features
        .filter(([, is_enabled]) => is_enabled) // ... but only enabled ones
        .map(([id]) => parseInt(id)); // ... map to ids, and make sure ids are numbers (to ensure backend is happy)
    },
  },
  mounted() {
    EventBus.$on('plan_added', (payload) => {
      this.plans.push(payload);
      this.selected_plan = { ..._.find(this.plans, { 'id': payload.id }) };
      this.populateSelectedFeatures(payload);
    });
  },
  methods: {
    /**
     * For the `selected_plan`, reassigns `this.staged_features` according to that plan's features.
     *
     * This is a full list of all toggleable features, which have their ids mapped to true or false values
     * depending on whether the plan's `.features` array contains them or not.
     */
    populateSelectedFeatures(selected_plan) {
      // staged features is defined as an assoc array mapping feature id to a bool value
      // { 0: false, 1: true, 2: true, ..., 17: false } etc.
      // true: feature is included in this plan
      // false: feature is not included
      this.staged_features = this.features.reduce((list, feature) => {
        list[feature.id] = selected_plan.features.find(plan_feature => plan_feature.id === feature.id) !== undefined;
        return list;
      }, {});

      // fills selected feed types
      this.allowed_feed_types = this.feed_types.reduce((list, feed_type) => {
        list[feed_type.id] = selected_plan.feed_types.find(feed_type_plan => feed_type_plan.id === feed_type.id) !== undefined;
        return list;
      }, {});

      this.selectPlanType(selected_plan);
    },
    async updatePlan() {
      try {
        const [updated_plan] = (await axios.patch(`/api/v1/admin/plans/${this.selected_plan.id}`, {
          label: this.selected_plan.label,
          feature_ids: this.enabled_staged_feature_ids,
          allowed_feed_types: this.allowed_feed_types,
          plan_type: this.selected_plan_type?.toLowerCase(),
        })).data.data;

        toastr.success('Plan updated');

        const index = this.plans.findIndex(plan => plan.id === updated_plan.id);
        if (index > -1) {
          this.plans.splice(index, 1, updated_plan);
        }
      } catch (err) {
        window.handleAxiosError(err);
      }
    },
    async deletePlan() {
      const plan_id = this.selected_plan.id;
      try {
        await axios.delete(`/api/v1/admin/plans/${plan_id}`).then(() => {
          this.plans = _.filter(this.plans, plan => plan.id != plan_id);
          this.selected_plan = null;
        });
      } catch (err) {
        window.handleAxiosError(err);
      }
    },
  },
};
</script>
